import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../auth/msalConfig";
import { theme, tint } from "../theme";

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;
const WS_BASE  = import.meta.env.VITE_WS_URL as string;

// The batch's own pipeline ends with a built, published model. Rolling that
// model out to the device groups is done per model on the Devices page, so the
// rollout rings are not stages here any more (their labels stay below because
// older batches still have events filed under them).
// "auto_labeling" (the central model's pre-annotation) is not shown either:
// it happens inside "Send for manual relabeling" in the same instant the batch
// is created, so it was always a chip that appeared already ticked.
const STAGES = [
  "collecting", "detection_qa", "annotation_qa", "training",
  "ci_build", "done",
];
type RetrainTarget = "edge" | "central" | "both";
const RETRAIN_TARGET_LABELS: Record<RetrainTarget, string> = {
  edge: "Edge model (ELF)",
  central: "Central model",
  both: "Combined models",
};

const EDGE_TRAINING_EXAMPLE = {
  train_mode: "full",
  tuner_max_trials: 30,
  space_json: [
    {
      inputBlocks: [{ type: "image", dimension: [[128, 128]], resizeMode: ["fit-short"] }],
      dspBlocks: [{ type: "image", channels: ["RGB"] }],
      learnBlocks: [[{
        augmentationPolicyImage: ["all", "none"],
        learningRate: [0.001, 0.00075, 0.0005],
        trainingCycles: [55, 60, 65, 70, 75, 80],
        id: 5,
        type: "keras-object-detection",
        model: ["fomo_mobilenet_v2_a35"],
      }]],
    },
  ],
};

type TrainingParams = Record<string, unknown>;

function isJsonObject(value: unknown): value is TrainingParams {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2) ?? "{}";
}

/** Parse the training-parameters editor text. `error` carries the browser's
 *  own JSON.parse message (it names the offending position), `count` is the
 *  number of top-level keys for the "Valid JSON · N parameters" note. */
function validateTrainingJson(text: string): { params: TrainingParams | null; error: string | null; count: number } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    return { params: null, error: err instanceof Error ? err.message : "parse error", count: 0 };
  }
  if (!isJsonObject(parsed)) return { params: null, error: "the training parameters must be a JSON object", count: 0 };
  return { params: parsed, error: null, count: Object.keys(parsed).length };
}
const QA_GATES = new Set(["detection_qa", "annotation_qa"]);
// Plain-language labels for internal stage slugs.
const STAGE_LABELS: Record<string, string> = {
  collecting:    "Collecting",
  detection_qa:  "Detection review",
  auto_labeling: "Central labeling",
  annotation_qa: "Review & approve labels",
  training:      "Training",
  ci_build:      "Packaging",
  staging:       "Test group",
  pilot:         "Pilot group",
  production:    "All devices",
  done:          "Model ready",
};
// Central-ML verdicts get the design system's slate-blue "info" hue: they are
// an informational classification, not a warning or an error, and the amber /
// red slots are already spoken for by the two event categories.
// Annotated as `string` rather than inferred: theme is `as const`, so an
// inferred type would be the literal "var(--status-info)" and every colour
// comparison against another token would be a type error.
const VIOLET: string = theme.info;

interface StageEvent {
  id: number;
  stage: string;
  status: string;
  actor: string;
  detail: string | null;
  at: string;
  metrics: Record<string, unknown> | null;
}

interface Approval {
  id: number;
  gate: string;
  decision: string;
  approver: string;
  note: string | null;
  at: string;
}

interface BatchSummary {
  id: number;
  name: string;
  stage: string;
  sample_count: number;
  cvat_task_id: number | null;
  model_version: string | null;
  impulse_id: number | null;
  package_url: string | null;
  retrain_target: RetrainTarget;
  central_model_version: string | null;
  central_model_url: string | null;
  central_model_metadata_url: string | null;
  central_model_sha256: string | null;
  created_at: string;
  updated_at: string;
  /** Outcome of the newest event in the batch's current stage (see backend
   *  BatchSummary.from_batch); null when nothing has run in that stage yet. */
  stage_status: "started" | "completed" | "failed" | "skipped" | null;
  stage_detail: string | null;
  stage_event_at: string | null;
}

interface BatchDetail extends BatchSummary {
  events: StageEvent[];
  approvals: Approval[];
}

interface CentralTrainingArtifact {
  name: string;
  url: string;
}

interface CentralTraining {
  run_id: string;
  version: string;
  metrics: Record<string, unknown>;
  map50: number | null;
  map50_95: number | null;
  visual_artifacts: CentralTrainingArtifact[];
}

/** Shared per-stage classification, so the chip list and the compact progress
 *  bar can never disagree about which stage is done / current / pending. */
function stagesFor(retrainTarget?: RetrainTarget): string[] {
  // A central ONNX candidate is uploaded directly to private Blob storage;
  // it has no ELF/package build.  Do not imply an absent packaging step.
  return retrainTarget === "central" ? STAGES.filter((stage) => stage !== "ci_build") : STAGES;
}

function stageState(s: string, i: number, stage: string, idx: number, pendingStage?: string | null) {
  const isCur = s === stage;
  // The stage a currently-offered approval button would advance to
  // (e.g. ci_build finished -> "staging" is next but isn't `stage` yet,
  // since the backend only moves forward once that gate is approved) —
  // flag it so it doesn't look untouched.
  const isPending = !isCur && s === pendingStage;
  // pendingStage being set at all means the *current* stage's own work
  // is done (that's exactly what unlocked the next stage's approval) —
  // so it should read as complete (green ✓), not "still active" gold.
  // "done" is a terminal state too — being "current" there means the
  // whole batch finished, not that something is actively in progress.
  const isCurDone = isCur && (pendingStage != null || stage === "done");
  return { isPending, isDone: i < idx || isCurDone, isActive: isCur && !isCurDone };
}

/** The detail panel's full stage list: one chip per stage, so an operator can
 *  read the whole pipeline and see exactly where the batch sits. */
function StageBar({ stage, pendingStage, retrainTarget }: {
  stage: string; pendingStage?: string | null; retrainTarget?: RetrainTarget;
}) {
  const stages = stagesFor(retrainTarget);
  const idx = stages.indexOf(stage);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 12 }}>
      {stages.map((s, i) => {
        const isQa = QA_GATES.has(s);
        const { isPending, isDone, isActive } = stageState(s, i, stage, idx, pendingStage);
        // Active = the brand fill (this is where work is happening now);
        // pending = the info hue, outlined, so it never competes with it.
        const border = isActive ? theme.gold : isPending ? VIOLET : isDone ? theme.success : theme.border;
        return (
          <span
            key={s}
            className={isActive || isPending ? "step-pulse" : undefined}
            style={{
              fontSize: 10,
              padding: "3px 8px",
              borderRadius: 10,
              border: `1px ${isQa || isPending ? "dashed" : "solid"} ${border}`,
              color: isActive ? theme.onGold : isPending ? VIOLET : isDone ? theme.success : theme.textMuted,
              background: isActive ? theme.gold : "transparent",
              fontWeight: isActive || isPending ? 700 : 400,
              whiteSpace: "nowrap",
            }}
          >
            {isDone ? "✓ " : ""}{STAGE_LABELS[s] ?? s}
          </span>
        );
      })}
    </div>
  );
}

/** The list row's compact form of the same information: one segment per stage.
 *  A ten-chip list per row would dominate the sidebar, so the row shows only
 *  the shape of the progress and names the current stage in words underneath. */
function StageBars({ stage, pendingStage, retrainTarget }: {
  stage: string; pendingStage?: string | null; retrainTarget?: RetrainTarget;
}) {
  const stages = stagesFor(retrainTarget);
  const idx = stages.indexOf(stage);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
      {stages.map((s, i) => {
        const { isPending, isDone, isActive } = stageState(s, i, stage, idx, pendingStage);
        const hue = isDone ? theme.success : isActive ? theme.gold : isPending ? VIOLET : null;
        return (
          <span
            key={s}
            title={STAGE_LABELS[s] ?? s}
            className={isActive || isPending ? "step-pulse" : undefined}
            style={{
              flex: 1,
              height: 5,
              borderRadius: 3,
              // Future stages read as empty outlines rather than filled bars,
              // so "how far along is this?" is legible at a glance.
              background: hue && !isPending ? hue : "transparent",
              border: `1px solid ${hue ?? theme.borderStrong}`,
            }}
          />
        );
      })}
    </div>
  );
}

/** The stage that comes next when the current one has finished its work but
 *  the batch has not moved on yet (e.g. the label review is complete and the
 *  operator has yet to start retraining). The chips then show the current
 *  stage ticked and the next one pending, instead of the finished stage still
 *  glowing gold as if someone were working on it. */
function nextStageIfCurrentDone(stage: string, currentDone: boolean,
                                retrainTarget?: RetrainTarget): string | null {
  if (!currentDone || stage === "done") return null;
  const stages = stagesFor(retrainTarget);
  const i = stages.indexOf(stage);
  return i >= 0 && i + 1 < stages.length ? stages[i + 1] : null;
}

function hasDeployableArtifact(b: BatchSummary): boolean {
  if (b.retrain_target === "both") return Boolean(b.package_url && b.central_model_url);
  return Boolean(b.retrain_target === "central" ? b.central_model_url : b.package_url);
}

/** A person's sign-in address as a small bordered chip — reads as "who", not
 *  as part of the sentence, and lines up down the list. */
function PersonChip({ name }: { name: string }) {
  return (
    <span
      title={name}
      style={{
        display: "inline-block", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        fontFamily: theme.fontMono, fontSize: 10, lineHeight: "16px",
        color: theme.textSecondary, background: theme.bgInput,
        border: `1px solid ${theme.border}`, borderRadius: 4, padding: "0 6px",
      }}
    >
      {name}
    </span>
  );
}

/** Every human click on a batch is stored as an Approval row (gate +
 *  decision); this turns the pair into a sentence and a colour. Unknown
 *  combinations fall back to the raw words so nothing is ever hidden. */
function describeAction(a: Approval): { text: string; tone: string } {
  const ring = STAGE_LABELS[a.gate] ?? a.gate;
  if (a.gate === "send_for_review")                         return { text: "Sent frames for manual relabeling", tone: theme.info };
  if (a.gate === "annotation_qa" && a.decision === "review_completed") return { text: "Completed the label review", tone: theme.success };
  if (a.gate === "annotation_qa" && a.decision === "approved") return { text: "Started retraining (EON tuner)", tone: theme.gold };
  if (a.gate === "annotation_qa" && a.decision === "rejected") return { text: "Rejected the label review", tone: theme.danger };
  if (a.gate === "detection_qa")                            return { text: `${a.decision === "approved" ? "Approved" : "Rejected"} the detection review`, tone: a.decision === "approved" ? theme.success : theme.danger };
  if (a.gate === "training" && a.decision === "stopped")   return { text: "Stopped the training", tone: theme.danger };
  if (["staging", "pilot", "production"].includes(a.gate)) return { text: `Deployed to ${ring}`, tone: theme.success };
  return { text: `${ring} · ${a.decision}`, tone: theme.textMuted };
}

function Spinner({ color = theme.gold, size = 13 }: { color?: string; size?: number }) {
  return (
    <span
      className="spin"
      style={{
        width: size, height: size, borderRadius: "50%",
        border: `2px solid ${theme.border}`, borderTopColor: color, flexShrink: 0,
      }}
    />
  );
}

function cvatReviewDone(b: BatchDetail): boolean {
  return b.events.some((e) => e.stage === "annotation_qa" && e.status === "completed");
}

function lastEventOf(b: BatchDetail, stage: string): StageEvent | undefined {
  const evs = b.events.filter((e) => e.stage === stage);
  return evs[evs.length - 1];
}

/** The stage's last *verdict* — "completed" or "failed" — ignoring any
 *  "started" progress lines that follow it.
 *
 *  A failure must never be masked by a late progress report, and that is
 *  exactly what used to happen: a build died, yet the ADO poller reported
 *  "14/14 steps done (100%)" seconds later (a failed run's steps are all
 *  "completed" too). Being the newest event, that progress line hid the real
 *  failure and the batch showed as still building — for days. */
function lastVerdictOf(b: BatchDetail, stage: string): StageEvent | undefined {
  const evs = b.events.filter(
    (e) => e.stage === stage && (e.status === "completed" || e.status === "failed")
  );
  return evs[evs.length - 1];
}

interface TrainingPoint {
  epoch: number;
  map50: number | null;
  map50_95: number | null;
  train_loss: number | null;
  val_loss: number | null;
}

function metricNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function trainingPoints(b: BatchDetail): TrainingPoint[] {
  return b.events
    .filter((event) => event.stage === "training" && event.metrics)
    .map((event) => {
      const metrics = event.metrics ?? {};
      const epoch = metricNumber(metrics.epoch);
      return epoch == null ? null : {
        epoch,
        map50: metricNumber(metrics.map50),
        map50_95: metricNumber(metrics.map50_95),
        train_loss: metricNumber(metrics.train_loss),
        val_loss: metricNumber(metrics.val_loss),
      };
    })
    .filter((point): point is TrainingPoint => point !== null)
    .sort((a, z) => a.epoch - z.epoch)
    .filter((point, index, all) => index === all.findIndex((candidate) => candidate.epoch === point.epoch));
}

interface ChartLine {
  key: keyof Omit<TrainingPoint, "epoch">;
  label: string;
  color: string;
}

export function MetricChart({ title, points, lines }: { title: string; points: TrainingPoint[]; lines: ChartLine[] }) {
  const [hover, setHover] = useState<TrainingPoint | null>(null);
  const width = 600;
  const height = 220;
  const pad = { left: 42, right: 16, top: 18, bottom: 30 };
  const usableWidth = width - pad.left - pad.right;
  const usableHeight = height - pad.top - pad.bottom;
  const epochs = points.map((point) => point.epoch);
  const values = lines.flatMap((line) => points
    .map((point) => point[line.key])
    .filter((value): value is number => typeof value === "number"));
  if (!values.length) return null;
  const minValue = Math.min(0, ...values);
  const maxValue = Math.max(...values, 1e-9);
  const range = Math.max(maxValue - minValue, 1e-9);
  const minEpoch = Math.min(...epochs);
  const maxEpoch = Math.max(...epochs);
  const epochRange = Math.max(maxEpoch - minEpoch, 1);
  const x = (epoch: number) => pad.left + ((epoch - minEpoch) / epochRange) * usableWidth;
  const y = (value: number) => pad.top + (1 - (value - minValue) / range) * usableHeight;
  const pathFor = (line: ChartLine) => points
    .filter((point) => typeof point[line.key] === "number")
    .map((point, index) => `${index === 0 ? "M" : "L"}${x(point.epoch).toFixed(1)},${y(point[line.key] as number).toFixed(1)}`)
    .join(" ");
  const tooltipX = hover ? Math.min(width - 172, Math.max(pad.left, x(hover.epoch) + 10)) : 0;
  const format = (value: number | null) => value == null ? "—" : value.toFixed(4);

  return (
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ color: theme.textSecondary, fontSize: theme.fontSizeXs, fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${title} by epoch`}
        style={{ width: "100%", height: "auto", display: "block", background: theme.bgInput, borderRadius: 5 }}
        onMouseLeave={() => setHover(null)}
      >
        {[0, 0.5, 1].map((fraction) => {
          const value = maxValue - fraction * range;
          return (
            <g key={fraction}>
              <line x1={pad.left} x2={width - pad.right} y1={pad.top + fraction * usableHeight} y2={pad.top + fraction * usableHeight} stroke={theme.border} strokeWidth="1" />
              <text x={pad.left - 6} y={pad.top + fraction * usableHeight + 4} textAnchor="end" fill={theme.textMuted} fontSize="10">{value.toFixed(2)}</text>
            </g>
          );
        })}
        <text x={pad.left} y={height - 8} fill={theme.textMuted} fontSize="10">epoch {minEpoch}</text>
        <text x={width - pad.right} y={height - 8} textAnchor="end" fill={theme.textMuted} fontSize="10">epoch {maxEpoch}</text>
        {lines.map((line) => <path key={line.key} d={pathFor(line)} fill="none" stroke={line.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />)}
        {lines.flatMap((line) => points
          .filter((point) => typeof point[line.key] === "number")
          .map((point) => (
            <circle
              key={`${line.key}-${point.epoch}`}
              cx={x(point.epoch)}
              cy={y(point[line.key] as number)}
              r="4"
              fill={theme.bgCard}
              stroke={line.color}
              strokeWidth="2"
              onMouseEnter={() => setHover(point)}
            />
          )))}
        {hover && (
          <g pointerEvents="none">
            <rect x={tooltipX} y={pad.top} width="162" height="74" rx="4" fill={theme.bgCard} stroke={theme.borderStrong} />
            <text x={tooltipX + 9} y={pad.top + 17} fill={theme.textPrimary} fontSize="11" fontWeight="700">Epoch {hover.epoch}</text>
            <text x={tooltipX + 9} y={pad.top + 34} fill={theme.textSecondary} fontSize="10">mAP50 {format(hover.map50)} · mAP50-95 {format(hover.map50_95)}</text>
            <text x={tooltipX + 9} y={pad.top + 50} fill={theme.textSecondary} fontSize="10">train loss {format(hover.train_loss)}</text>
            <text x={tooltipX + 9} y={pad.top + 66} fill={theme.textSecondary} fontSize="10">val loss {format(hover.val_loss)}</text>
          </g>
        )}
      </svg>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 5 }}>
        {lines.map((line) => <span key={line.key} style={{ color: line.color, fontSize: theme.fontSize2Xs }}>● {line.label}</span>)}
      </div>
    </div>
  );
}

function RetrainSelectionDialog({ target, busy, submitError, onClearError, onChange, onCancel, onConfirm }: {
  target: RetrainTarget;
  busy: boolean;
  submitError?: string | null;
  onClearError?: () => void;
  onChange: (target: RetrainTarget) => void;
  onCancel: () => void;
  onConfirm: (edgeParams?: TrainingParams) => void | Promise<void>;
}) {
  const [edgeText, setEdgeText] = useState(() => prettyJson(EDGE_TRAINING_EXAMPLE));
  // Validated on every keystroke rather than on confirm, so a typo is pointed
  // out where it happens instead of after the operator has already clicked
  // "Start retraining" and is wondering why nothing started.
  const edgeParsed = useMemo(() => validateTrainingJson(edgeText), [edgeText]);
  const options: Array<{ value: RetrainTarget; title: string; detail: string; disabled?: boolean }> = [
    { value: "edge", title: RETRAIN_TARGET_LABELS.edge, detail: "Retrain the Edge Impulse detector and build a new router ELF." },
    { value: "central", title: RETRAIN_TARGET_LABELS.central, detail: "Retrain the central ONNX detector." },
    { value: "both", title: RETRAIN_TARGET_LABELS.both, detail: "Retrain both models from the reviewed dataset." },
  ];
  const editorLabel: CSSProperties = {
    display: "block", color: theme.textPrimary, fontSize: theme.fontSizeXs,
    fontWeight: 700, marginBottom: 5,
  };
  const editorStyle: CSSProperties = {
    width: "100%", boxSizing: "border-box", resize: "vertical", minHeight: 220,
    padding: "9px 10px", borderRadius: 5,
    border: `1px solid ${edgeParsed.error ? theme.danger : theme.borderStrong}`,
    background: theme.bgInput, color: theme.textPrimary, fontFamily: theme.fontMono,
    fontSize: 11, lineHeight: 1.45,
  };
  const usesEdge = target === "edge" || target === "both";
  const canStart = !busy && (!usesEdge || edgeParsed.params !== null);

  const confirm = () => {
    if (!usesEdge || edgeParsed.params) void onConfirm(edgeParsed.params ?? undefined);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="retrain-selection-title"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(26,28,30,0.62)", backdropFilter: "blur(5px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}
    >
      <div style={{ width: "min(760px, 100%)", maxHeight: "calc(100vh - 40px)", overflowY: "auto", background: theme.bgCard, border: `1px solid ${theme.borderStrong}`, borderRadius: 10, padding: 22, boxShadow: "0 12px 40px rgba(0,0,0,0.24)" }}>
        <h2 id="retrain-selection-title" style={{ ...sectionHeading, fontSize: 19 }}>Choose what to retrain</h2>
        <p style={{ color: theme.textSecondary, fontSize: theme.fontSizeXs, lineHeight: 1.5, margin: "8px 0 14px" }}>
          The reviewed dataset is ready. Select the model update and adjust its training parameters before starting.
        </p>
        <div style={{ display: "grid", gap: 8 }}>
          {options.map((option) => {
            const active = target === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={busy || option.disabled}
                aria-pressed={active}
                onClick={() => { if (!option.disabled) onChange(option.value); }}
                style={{
                  textAlign: "left", padding: "11px 13px", borderRadius: 6,
                  border: `1.5px solid ${active ? theme.gold : theme.border}`,
                  background: active ? theme.goldSoft : theme.bgInput,
                  color: theme.textPrimary, cursor: busy || option.disabled ? "default" : "pointer",
                  opacity: busy || option.disabled ? 0.55 : 1,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 9, fontSize: theme.fontSizeSm, fontWeight: 700 }}>
                  <span style={{ width: 13, height: 13, borderRadius: "50%", border: `2px solid ${active ? theme.gold : theme.borderStrong}`, background: active ? theme.gold : "transparent", boxShadow: active ? `inset 0 0 0 3px ${theme.bgCard}` : "none" }} />
                  {option.title}
                </span>
                <span style={{ display: "block", margin: "5px 0 0 22px", color: theme.textSecondary, fontSize: theme.fontSize2Xs }}>{option.detail}</span>
              </button>
            );
          })}
        </div>
        {usesEdge && <div style={{ marginTop: 16 }}>
            <label htmlFor="edge-training-params" style={editorLabel}>Edge Impulse training JSON</label>
            <div style={{ color: theme.textMuted, fontSize: theme.fontSize2Xs, lineHeight: 1.45, marginBottom: 6 }}>
              Edit the EON Tuner mode, trial budget, or search space. The reviewed dataset and deployment approval flow stay unchanged.
            </div>
            <textarea
              id="edge-training-params"
              value={edgeText}
              disabled={busy}
              spellCheck={false}
              aria-invalid={edgeParsed.error !== null}
              aria-describedby="edge-training-params-status"
              onChange={(event) => { setEdgeText(event.target.value); onClearError?.(); }}
              style={editorStyle}
            />
            <div
              id="edge-training-params-status"
              role={edgeParsed.error ? "alert" : undefined}
              style={{ marginTop: 5, fontSize: theme.fontSize2Xs, color: edgeParsed.error ? theme.danger : theme.textMuted }}
            >
              {edgeParsed.error
                ? `Invalid JSON: ${edgeParsed.error}`
                : `Valid JSON · ${edgeParsed.count} parameter${edgeParsed.count === 1 ? "" : "s"}`}
            </div>
          </div>}
        {submitError && <div role="alert" style={{ marginTop: 12, color: theme.danger, background: theme.dangerSoft, border: `1px solid ${theme.danger}`, borderRadius: 5, padding: "8px 10px", fontSize: theme.fontSize2Xs }}>{submitError}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" disabled={busy} onClick={onCancel} style={{ background: "transparent", color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: 5, padding: "8px 13px", fontSize: theme.fontSizeXs, cursor: busy ? "default" : "pointer" }}>Cancel</button>
          <button type="button" disabled={!canStart} onClick={confirm} style={{ background: theme.gold, color: theme.onGold, border: "none", borderRadius: 5, padding: "8px 15px", fontSize: theme.fontSizeXs, fontWeight: 700, cursor: canStart ? "pointer" : "default", opacity: canStart ? 1 : 0.65 }}>{busy ? "Starting…" : "Start retraining"}</button>
        </div>
      </div>
    </div>
  );
}

function TrainingArtifactPreviewDialog({ artifact, onClose }: {
  artifact: CentralTrainingArtifact;
  onClose: () => void;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="training-artifact-preview-title"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(26,28,30,0.62)", backdropFilter: "blur(5px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div style={{ width: "min(1200px, 100%)", maxHeight: "calc(100vh - 40px)", overflow: "auto", background: theme.bgCard, border: `1px solid ${theme.borderStrong}`, borderRadius: 10, padding: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.24)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <h2 id="training-artifact-preview-title" style={{ ...sectionHeading, fontSize: theme.fontSizeSm }}>{artifact.name}</h2>
          <button type="button" onClick={onClose} style={{ background: "transparent", color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: 5, padding: "6px 11px", fontSize: theme.fontSizeXs, cursor: "pointer" }}>Close</button>
        </div>
        <img src={artifact.url} alt={artifact.name} style={{ width: "100%", maxHeight: "calc(100vh - 130px)", objectFit: "contain", display: "block", background: theme.bgInput }} />
      </div>
    </div>
  );
}

/** Context-aware "what happens next" panel: shows the single relevant human
 *  action (a pulsing button) or a waiting/in-progress indicator (a spinner)
 *  for the batch's current stage. */
function NextStep({ b, onApprove, approving, activeRetrain, onStop, stopping }: {
  b: BatchDetail;
  onApprove: (gate: string) => void;
  approving: string | null;
  /** The batch whose EON Tuner run currently holds the single training slot,
   *  or null when nothing is training. */
  activeRetrain: { id: number; name: string | null } | null;
  onStop: () => void;
  stopping: boolean;
}) {
  const stage = b.stage;
  const busy = approving !== null;
  // Only one EON Tuner run may be active at a time, so another batch's run
  // locks this one's retrain buttons until it finishes or is stopped.
  const blockedBy = activeRetrain && activeRetrain.id !== b.id ? activeRetrain : null;
  const isActiveRetrain = activeRetrain?.id === b.id;

  // Stop is a two-click action: the first arms "Confirm stop", which reverts
  // by itself if the operator does not follow through within a few seconds.
  // (No window.confirm — browser dialogs are not used in this app.)
  const [stopArmed, setStopArmed] = useState(false);
  useEffect(() => {
    if (!stopArmed) return;
    const timer = window.setTimeout(() => setStopArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [stopArmed]);
  useEffect(() => { setStopArmed(false); }, [b.id, stopping]);

  const box: CSSProperties = {
    marginTop: 14, padding: "12px 14px", borderRadius: 5,
    border: `1px solid ${theme.border}`, background: theme.bgInput,
    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
  };
  const label: CSSProperties = { color: theme.textSecondary, fontSize: theme.fontSizeXs };

  // Every gate here is *the* action for the panel, so they all get the single
  // primary treatment — the brand fill. Status meaning stays with the
  // semantic colours on the message text beside it.
  const actionButton = (text: string, gate: string) => (
    <button
      className={busy ? undefined : "btn-pulse"}
      disabled={busy}
      onClick={() => onApprove(gate)}
      style={{
        background: theme.gold, color: theme.onGold,
        border: "none", borderRadius: 5, padding: "8px 16px",
        fontSize: theme.fontSizeXs, fontWeight: 700, cursor: busy ? "default" : "pointer",
        opacity: busy ? 0.6 : 1,
      }}
    >
      {approving === gate ? "…" : text}
    </button>
  );

  // The retrain gates specifically: disabled while another batch trains, with
  // the reason spelled out directly underneath instead of a silent grey button.
  const retrainButton = (text: string) => {
    if (!blockedBy) return actionButton(text, "annotation_qa");
    return (
      <>
        <button
          disabled
          style={{
            background: theme.gold, color: theme.onGold,
            border: "none", borderRadius: 5, padding: "8px 16px",
            fontSize: theme.fontSizeXs, fontWeight: 700, cursor: "default", opacity: 0.45,
          }}
        >
          {text}
        </button>
        <span style={{ ...mutedNote, flexBasis: "100%" }}>
          Training is already running for batch #{blockedBy.id}
          {blockedBy.name ? ` ("${blockedBy.name}")` : ""}. Only one EON tuner run can be active at a time.
        </span>
      </>
    );
  };

  const stopButton = (
    <button
      disabled={stopping}
      onClick={() => { if (stopArmed) { setStopArmed(false); onStop(); } else setStopArmed(true); }}
      title={stopArmed ? "Click again to stop this training run" : "Stop the running EON Tuner job"}
      style={{
        marginLeft: "auto",
        background: stopArmed ? theme.dangerSoft : theme.bgCard, color: theme.danger,
        border: `1px solid ${theme.danger}`, borderRadius: 5, padding: "7px 14px",
        fontSize: theme.fontSizeXs, fontWeight: 700, cursor: stopping ? "default" : "pointer",
        opacity: stopping ? 0.6 : 1,
      }}
    >
      {stopping ? "Stopping…" : stopArmed ? "Confirm stop" : "Stop training"}
    </button>
  );

  // The end of this page's responsibility: the model exists. Where it runs
  // (test group, pilot, all devices) is decided per model on the Devices page,
  // so the panel names the artefact and hands the operator over.
  const modelReady = (batch: BatchDetail) => (
    <div style={{ ...box, flexWrap: "wrap" }}>
      <span style={{ ...label, color: theme.success }}>✓ Model ready</span>
      {batch.model_version && (
        <span style={{ ...label, fontFamily: theme.fontMono, color: theme.textPrimary }}>
          {batch.model_version}{batch.impulse_id != null ? ` · impulse ${batch.impulse_id}` : ""}
        </span>
      )}
      <Link
        to={`/devices?model=${batch.id}`}
        style={{
          marginLeft: "auto", background: theme.gold, color: theme.onGold, borderRadius: 5,
          padding: "8px 16px", fontSize: theme.fontSizeXs, fontWeight: 700, textDecoration: "none",
        }}
      >
        Deploy…
      </Link>
    </div>
  );

  const waiting = (text: string, trailing?: ReactNode) => (
    <div style={box}><Spinner /><span style={label}>{text}</span>{trailing}</div>
  );

  if (stage === "detection_qa")
    return <div style={box}><span style={label}>Double-checked these detections?</span>{actionButton("Approve", "detection_qa")}</div>;

  if (stage === "auto_labeling")
    return waiting("The active central model is labeling the objects in the images…");

  if (stage === "annotation_qa") {
    if (!cvatReviewDone(b)) {
      return (
        <div style={box}>
          <Spinner />
          <span style={label}>Waiting for the label review to be completed.</span>
          {b.cvat_task_id != null && (
            <Link
              className="btn-pulse"
              to={`/review/${b.cvat_task_id}`}
              style={{
                background: theme.gold, color: theme.onGold, borderRadius: 5,
                padding: "8px 16px", fontSize: theme.fontSizeXs, fontWeight: 700, textDecoration: "none",
              }}
            >
              Open review tool
            </Link>
          )}
        </div>
      );
    }
    return (
      <div style={box}>
        <span style={{ ...label, color: theme.success }}>✓ Review complete.</span>
        {retrainButton("Choose models and start retraining")}
      </div>
    );
  }

  // The two retrainers report independently. An Edge package or a later
  // progress event must not turn a failed combined attempt into success.
  if (b.stage_status === "failed" && (stage === "training" || stage === "ci_build" || stage === "done"))
    return (
      <div style={box}>
        <span style={{ ...label, color: theme.danger }}>✗ Retraining failed: {b.stage_detail}</span>
        {isActiveRetrain ? stopButton : retrainButton("Retry training")}
      </div>
    );

  if (stage === "training") {
    return waiting(
      `Training ${RETRAIN_TARGET_LABELS[b.retrain_target] ?? RETRAIN_TARGET_LABELS.both}… ${lastEventOf(b, "training")?.detail ?? ""}`,
      // A run without a verdict yet is stoppable; so is whatever the backend
      // reports as the active run, even if this panel's events lag behind.
      isActiveRetrain ? stopButton : undefined,
    );
  }

  if (stage === "ci_build") {
    const last = lastVerdictOf(b, "ci_build");
    if (last?.status === "failed")
      return (
        <div style={box}>
          <span style={{ ...label, color: theme.danger }}>✗ Build failed: {last.detail}</span>
        </div>
      );
    if (last?.status === "completed" && hasDeployableArtifact(b)) return modelReady(b);
    // Same as the training row: the latest progress note ("ADO build 7/13:
    // Sign package") rides along so the panel moves while the build runs.
    return waiting(`Packaging and signing the update… ${lastEventOf(b, "ci_build")?.detail ?? ""}`);
  }

  if (stage === "done") return modelReady(b);

  return <div style={box}><Spinner /><span style={label}>Collecting flagged examples…</span></div>;
}

/** What a batch is doing right now, reduced to the four things an operator
 *  scanning the list wants to tell apart: broken, busy, waiting for a person,
 *  or finished. Derived from the current stage's newest event plus whether the
 *  backend says this batch holds the training slot. */
type CardState = "failed" | "running" | "waiting" | "done";

function cardState(b: BatchSummary, isActiveRetrain: boolean): CardState {
  if (b.stage_status === "failed") return "failed";
  // A published package is the finish line; batches built before the stage
  // machine learnt that still sit at ci_build with a package_url.
  if (b.stage === "done" || (b.stage === "ci_build" && hasDeployableArtifact(b))) return "done";
  if (isActiveRetrain) return "running";
  // "started" in a machine stage means work is in flight; in a human stage it
  // is just the gate opening, so the batch is waiting for someone.
  if (b.stage_status === "started" && (b.stage === "training" || b.stage === "ci_build")) return "running";
  return "waiting";
}

const CARD_TONE: Record<CardState, string> = {
  failed:  theme.danger,
  running: theme.info,
  waiting: theme.gold,
  done:    theme.success,
};

function BatchRow({ b, active, isActiveRetrain, onClick }: {
  b: BatchSummary; active: boolean; isActiveRetrain: boolean; onClick: () => void;
}) {
  const state = cardState(b, isActiveRetrain);
  const tone = CARD_TONE[state];
  const stageName = STAGE_LABELS[b.stage] ?? b.stage;
  return (
    <div
      onClick={onClick}
      style={{
        background: state === "failed" ? tint(theme.danger, 5) : theme.bgCard,
        // The rail carries the state colour; the outline stays reserved for
        // "this is the selected batch" so the two never compete. Longhands
        // only: mixing the `border` shorthand with `borderLeft` made React
        // reset the rail whenever the outline colour changed on selection —
        // the rail's own value was unchanged, so it was never written back.
        borderWidth: "1px 1px 1px 4px",
        borderStyle: "solid",
        borderColor: `${active ? theme.gold : theme.border} ${active ? theme.gold : theme.border} ${active ? theme.gold : theme.border} ${tone}`,
        borderRadius: theme.radius,
        padding: "12px 14px 12px 12px",
        marginBottom: 8,
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        {/* minWidth 0 + ellipsis: batch names are operator-typed and can be long,
            and the row must never widen the column or push the date off-screen. */}
        <b style={{
          color: theme.textPrimary, fontSize: theme.fontSizeSm,
          minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          #{b.id} {b.name}
        </b>
        <span style={{ color: theme.textMuted, fontSize: 11, flexShrink: 0 }}>
          {new Date(b.updated_at).toLocaleString()}
        </span>
      </div>
      <div style={{ color: theme.textSecondary, fontSize: theme.fontSize2Xs, marginTop: 3 }}>
        {b.sample_count} samples
        {` · ${RETRAIN_TARGET_LABELS[b.retrain_target] ?? RETRAIN_TARGET_LABELS.both}`}
        {b.model_version ? ` · model ${b.model_version}` : ""}
        {b.cvat_task_id ? ` · Item #${b.cvat_task_id}` : ""}
      </div>
      <StageBars
        stage={b.stage}
        retrainTarget={b.retrain_target}
        pendingStage={nextStageIfCurrentDone(b.stage, b.stage_status === "completed", b.retrain_target)}
      />
      {/* The bar shows how far; this line says what is happening there — and
          whether it needs a person, is still running, or broke. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6, minWidth: 0,
        fontSize: 11, marginTop: 6, fontWeight: 500,
        color: state === "waiting" ? theme.goldText : tone,
      }}>
        {state === "running" && <Spinner color={theme.info} />}
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {state === "done" && "✓ Model ready — deploy it from Devices"}
          {state === "failed" && `✗ Batch failed${b.stage_detail ? ` — ${b.stage_detail}` : ""}`}
          {state === "running" && `${stageName} running${b.stage_detail ? ` — ${b.stage_detail}` : "…"}`}
          {state === "waiting" && (b.stage_status === "completed"
            ? `✓ ${stageName} complete — next: ${STAGE_LABELS[nextStageIfCurrentDone(b.stage, true) ?? ""] ?? "…"}`
            : `Now: ${stageName}`)}
        </span>
      </div>
    </div>
  );
}

export function OrchestratorPage() {
  const { instance, accounts } = useMsal();
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [selected, setSelected] = useState<BatchDetail | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // The selected batch lives in the URL (?batch=ID) so the label editor can
  // hand the operator straight back to the batch they just reviewed, and so a
  // reload keeps the panel open.
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [retrainDialogOpen, setRetrainDialogOpen] = useState(false);
  const [retrainTarget, setRetrainTarget] = useState<RetrainTarget>("both");
  const [centralTraining, setCentralTraining] = useState<CentralTraining | null>(null);
  const [centralArtifactPreview, setCentralArtifactPreview] = useState<CentralTrainingArtifact | null>(null);
  const [centralDeploying, setCentralDeploying] = useState(false);
  // Which batch holds the single EON Tuner slot right now (null = none). Seeded
  // from GET /retrain/active and kept current by the WS "retrain_active" message.
  const [activeRetrainBatchId, setActiveRetrainBatchId] = useState<number | null>(null);
  const [stopping, setStopping] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  const authedFetch = useCallback(async (path: string, opts?: RequestInit) => {
    const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
    const resp = await fetch(`${API_BASE}/orchestrator/api${path}`, {
      ...opts,
      headers: { ...(opts?.headers ?? {}), Authorization: `Bearer ${r.accessToken}`,
                 ...(opts?.body ? { "Content-Type": "application/json" } : {}) },
    });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json() as { detail?: string };
        if (body.detail) detail = body.detail;
      } catch {
        // Keep the status-based error when the response is not JSON.
      }
      throw new Error(detail);
    }
    return resp.json();
  }, [instance, accounts]);

  const loadList = useCallback(async () => {
    try {
      const bs: BatchSummary[] = await authedFetch("/batches");
      setBatches(bs);
    } catch (err) {
      console.error("Failed to load batches:", err);
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const loadActiveRetrain = useCallback(async () => {
    try {
      const active: { batch_id: number | null } = await authedFetch("/retrain/active");
      setActiveRetrainBatchId(active.batch_id);
    } catch (err) {
      console.error("Failed to load active retrain:", err);
    }
  }, [authedFetch]);

  const openBatch = useCallback(async (id: number) => {
    setCentralArtifactPreview(null);
    setSelectedId(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("batch", String(id));
      return next;
    }, { replace: true });
    try {
      const b: BatchDetail = await authedFetch(`/batches/${id}`);
      setSelected(b);
      if ((b.retrain_target === "central" || b.retrain_target === "both") && b.central_model_metadata_url) {
        const training: CentralTraining = await authedFetch(`/batches/${id}/central-training`);
        setCentralTraining(training);
      } else {
        setCentralTraining(null);
      }
    } catch (err) {
      console.error("Failed to load batch:", err);
      setCentralTraining(null);
    }
  }, [authedFetch]);

  const handleCentralDeploy = async () => {
    if (!centralTraining || selectedId == null) return;
    setApprovalError(null);
    setCentralDeploying(true);
    try {
      await authedFetch(`/central-models/${encodeURIComponent(centralTraining.run_id)}/activate`, { method: "POST" });
      await loadList();
      await openBatch(selectedId);
    } catch (err) {
      console.error("Failed to activate central model:", err);
      setApprovalError(err instanceof Error ? err.message : "Failed to activate central model");
    } finally {
      setCentralDeploying(false);
    }
  };

  const handleDelete = async () => {
    if (selectedId == null) return;
    if (!window.confirm("Delete this item and its history? This cannot be undone.")) return;
    try {
      await authedFetch(`/batches/${selectedId}`, { method: "DELETE" });
      setSelected(null);
      setSelectedId(null);
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("batch"); return next; }, { replace: true });
      await loadList();
    } catch (err) {
      console.error("Failed to delete batch:", err);
    }
  };

  const submitApproval = async (
    gate: string,
    target?: RetrainTarget,
    edgeParams?: TrainingParams,
  ): Promise<boolean> => {
    if (selectedId == null) return false;
    const approver = accounts[0]?.username ?? window.prompt("Approver?", "operator");
    if (!approver) return false;
    if (gate === "annotation_qa") setApprovalError(null);
    setApproving(gate);
    try {
      await authedFetch(`/batches/${selectedId}/approvals`, {
        method: "POST",
        body: JSON.stringify({
          gate, decision: "approved", approver,
          ...(gate === "annotation_qa" ? {
            retrain_target: target ?? "edge",
            ...((target === "edge" || target === "both")
              ? { edge_training_params: edgeParams ?? {} }
              : {}),
          } : {}),
        }),
      });
      await loadList();
      await openBatch(selectedId);
      return true;
    } catch (err) {
      console.error("Failed to approve gate:", err);
      if (gate === "annotation_qa") {
        setApprovalError(err instanceof Error ? err.message : "Failed to start retraining");
      }
      return false;
    } finally {
      setApproving(null);
    }
  };

  const handleApprove = (gate: string) => {
    if (gate === "annotation_qa") {
      setRetrainTarget("edge");
      setApprovalError(null);
      setRetrainDialogOpen(true);
      return;
    }
    void submitApproval(gate);
  };

  const confirmRetrain = async (edgeParams?: TrainingParams) => {
    const accepted = await submitApproval("annotation_qa", retrainTarget, edgeParams);
    if (accepted) setRetrainDialogOpen(false);
  };

  const handleStopTraining = async () => {
    if (selectedId == null) return;
    const actor = accounts[0]?.username ?? "operator";
    setApprovalError(null);
    setStopping(true);
    try {
      await authedFetch(`/batches/${selectedId}/retrain/stop`, {
        method: "POST",
        body: JSON.stringify({ actor }),
      });
      await loadActiveRetrain();
      await loadList();
      await openBatch(selectedId);
    } catch (err) {
      console.error("Failed to stop training:", err);
      setApprovalError(err instanceof Error ? err.message : "Failed to stop training");
    } finally {
      setStopping(false);
    }
  };

  useEffect(() => { loadList(); loadActiveRetrain(); }, [loadList, loadActiveRetrain]);

  // Open the batch named in the URL once, on arrival (from the label editor or
  // a reload). Later clicks update the param themselves via openBatch.
  const requestedBatch = searchParams.get("batch");
  useEffect(() => {
    const id = requestedBatch ? Number(requestedBatch) : NaN;
    if (Number.isInteger(id) && id > 0 && id !== selectedId) void openBatch(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedBatch]);

  // onmessage is set up once per connect() and would otherwise close over
  // whatever `selectedId` was at that time (null, on first mount) — so the
  // open batch's detail panel (StageBar/NextStep/events, what an operator is
  // actually watching during a multi-hour retrain) never live-updated even
  // though the sidebar list did. A ref sidesteps that without reconnecting
  // every time the user picks a different batch.
  const selectedIdRef = useRef<number | null>(null);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // WebSocket: live refresh on any batch update. Azure App Service can drop
  // an idle/long-lived WS (proxy timeout, worker recycle) — exactly the kind
  // of multi-hour gap an EON Tuner run sits in — so without a reconnect the
  // page silently stops updating until the operator manually refreshes.
  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;
    let retryDelay = 1000;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      if (cancelled || !accounts.length) return;
      try {
        const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
        if (cancelled) return;
        ws = new WebSocket(`${WS_BASE}/orchestrator/ws?token=${encodeURIComponent(r.accessToken)}`);
        wsRef.current = ws;
        ws.onopen = () => { retryDelay = 1000; };
        ws.onmessage = (msg) => {
          // "retrain_active" carries its payload inline and changes nothing
          // about the batch list itself; every other message type means some
          // batch changed, so refetch as before.
          try {
            const parsed = JSON.parse(String(msg.data)) as { type?: string; data?: { batch_id?: number | null } };
            if (parsed.type === "retrain_active") {
              setActiveRetrainBatchId(parsed.data?.batch_id ?? null);
              return;
            }
          } catch {
            // Not JSON — fall through to the generic refresh.
          }
          loadList();
          loadActiveRetrain();
          if (selectedIdRef.current != null) openBatch(selectedIdRef.current);
        };
        ws.onclose = () => {
          if (cancelled) return;
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        };
      } catch (err) {
        console.error("Orchestrator WS auth failed:", err);
        if (!cancelled) retryTimer = setTimeout(connect, retryDelay);
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts]);

  // Display order only: the log reads newest-first, but every rule that walks
  // the history (currentTrainingVerdict, lastEventOf, …) keeps working on the
  // untouched chronological `events` array.
  const actionsNewestFirst = useMemo(() => {
    if (!selected) return [];
    return [...selected.approvals].sort((a, z) =>
      (new Date(z.at).getTime() - new Date(a.at).getTime()) || (z.id - a.id));
  }, [selected]);

  const eventsNewestFirst = useMemo(() => {
    if (!selected) return [];
    return [...selected.events].sort((a, z) =>
      (new Date(z.at).getTime() - new Date(a.at).getTime()) || (z.id - a.id));
  }, [selected]);
  // The backend files every progress note as "started" (tuner 1/5, 2/5, "ADO
  // build queued"…), so the raw word repeats down the whole log. Show those
  // as PROGRESS, and only the newest one — the step that is actually in flight
  // right now, i.e. nothing has completed or failed since — as RUNNING.
  // A finished batch has nothing in flight even if its newest row is a
  // progress note: the ADO step poller can file "build 11/13" a few seconds
  // after the build's own completion callback already closed the batch.
  const runningEventId =
    eventsNewestFirst[0]?.status === "started" && selected?.stage !== "done"
      ? eventsNewestFirst[0].id : null;

  const activeRetrain = useMemo(() => {
    if (activeRetrainBatchId == null) return null;
    return { id: activeRetrainBatchId, name: batches.find((b) => b.id === activeRetrainBatchId)?.name ?? null };
  }, [activeRetrainBatchId, batches]);

  return (
    <>
      <div style={{ padding: 24, maxWidth: 1280 }}>
      {/* auto-fit rather than a fixed two-column split: the nav rail eats 216px,
          so below ~700px of content the two panels stack instead of squeezing. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 18, marginTop: 18 }}>
      <section style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
          <h2 style={sectionHeading}>Update batches</h2>
          <span style={{ fontSize: 11, color: theme.textMuted }}>created automatically when items are sent for review</span>
        </div>
        {loading && <div style={placeholderCard}>Loading…</div>}
        {!loading && batches.length === 0 && (
          <div style={placeholderCard}>No batches yet — one is created when items are sent for review.</div>
        )}
        {batches.map((b) => (
          <BatchRow
            key={b.id}
            b={b}
            active={b.id === selectedId}
            isActiveRetrain={activeRetrainBatchId === b.id}
            onClick={() => openBatch(b.id)}
          />
        ))}
      </section>

      <section style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
          <h2 style={sectionHeading}>Detail</h2>
        </div>
        {!selected && <div style={placeholderCard}>Select a batch to see its history.</div>}
        {selected && (
          <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: theme.radius, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <b style={{ color: theme.textPrimary, fontSize: theme.fontSizeSm }}>#{selected.id} {selected.name}</b>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: theme.textMuted, fontSize: theme.fontSize2Xs }}>
                  {selected.sample_count} samples · stage{" "}
                  <b style={{ color: theme.goldText }}>{STAGE_LABELS[selected.stage] ?? selected.stage}</b>
                </span>
                {selected.cvat_task_id != null ? (
                  <Link
                    to={`/review/${selected.cvat_task_id}?readonly=1`}
                    title="Browse this batch's images and labels without editing"
                    style={{
                      color: theme.textSecondary, textDecoration: "none",
                      border: `1px solid ${theme.border}`, borderRadius: 5,
                      padding: "4px 10px", fontSize: theme.fontSize2Xs, fontWeight: 600,
                    }}
                  >
                    View images
                  </Link>
                ) : (
                  <button
                    disabled
                    title="No review task for this batch"
                    style={{
                      background: "transparent", color: theme.textMuted,
                      border: `1px solid ${theme.border}`, borderRadius: 5,
                      padding: "4px 10px", fontSize: theme.fontSize2Xs, fontWeight: 600, cursor: "default", opacity: 0.6,
                    }}
                  >
                    View images
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  title="Delete this item and its history"
                  style={{
                    background: "transparent", color: theme.danger,
                    border: `1px solid ${theme.border}`, borderRadius: 5,
                    padding: "4px 10px", fontSize: theme.fontSize2Xs, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
            <StageBar
              stage={selected.stage}
              retrainTarget={selected.retrain_target}
              pendingStage={nextStageIfCurrentDone(selected.stage, lastVerdictOf(selected, selected.stage)?.status === "completed", selected.retrain_target)}
            />

            <NextStep
              b={selected}
              onApprove={handleApprove}
              approving={approving}
              activeRetrain={activeRetrain}
              onStop={() => void handleStopTraining()}
              stopping={stopping}
            />
            {centralTraining && (
              <section style={{ marginTop: 18, borderTop: `1px solid ${theme.border}`, paddingTop: 14 }}>
                <p style={{ ...eyebrow, margin: "0 0 6px" }}>Central training</p>
                <div style={{ color: theme.textSecondary, fontSize: theme.fontSize2Xs }}>
                  Run <b style={{ color: theme.textPrimary }}>{centralTraining.run_id}</b>
                  {centralTraining.map50 != null && ` · mAP50 ${(centralTraining.map50 * 100).toFixed(1)}%`}
                  {centralTraining.map50_95 != null && ` · mAP50-95 ${(centralTraining.map50_95 * 100).toFixed(1)}%`}
                </div>
                {selected.stage === "done" && (
                  <button
                    onClick={() => void handleCentralDeploy()}
                    disabled={centralDeploying}
                    style={{ marginTop: 10, background: theme.gold, color: theme.onGold, border: 0, borderRadius: 5, padding: "6px 10px", fontSize: theme.fontSize2Xs, fontWeight: 700, cursor: centralDeploying ? "wait" : "pointer", opacity: centralDeploying ? 0.7 : 1 }}
                  >
                    {centralDeploying ? "Deploying…" : `Deploy central model ID: ${centralTraining.run_id}`}
                  </button>
                )}
                {centralTraining.visual_artifacts.length > 0 && (
                  <div style={{ display: "grid", gap: 14, marginTop: 12 }}>
                    {[
                      ["Training graphs", centralTraining.visual_artifacts.filter((artifact) => !artifact.name.startsWith("test/"))],
                      ["Held-out test metrics", centralTraining.visual_artifacts.filter((artifact) => artifact.name.startsWith("test/"))],
                    ].map(([title, artifacts]) => {
                      const images = artifacts as CentralTraining["visual_artifacts"];
                      if (!images.length) return null;
                      return (
                        <div key={title as string}>
                          <p style={{ ...eyebrow, margin: "0 0 6px" }}>{title as string}</p>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                            {images.map((artifact) => (
                              <figure key={artifact.url} style={{ margin: 0, border: `1px solid ${theme.border}`, borderRadius: 5, padding: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => setCentralArtifactPreview(artifact)}
                                  title="Open enlarged image"
                                  style={{ display: "block", width: "100%", padding: 0, border: 0, background: "transparent", cursor: "zoom-in" }}
                                >
                                  <img src={artifact.url} alt={artifact.name} style={{ width: "100%", display: "block" }} />
                                </button>
                                <figcaption style={{ color: theme.textMuted, fontSize: 10, marginTop: 4 }}>{artifact.name}</figcaption>
                              </figure>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
            {/* The dialog shows this error itself while open; outside it (a
                failed stop, a 409 after the dialog closed) it needs a home. */}
            {approvalError && !retrainDialogOpen && (
              <div role="alert" style={{ marginTop: 8, color: theme.danger, background: theme.dangerSoft, border: `1px solid ${theme.danger}`, borderRadius: 5, padding: "8px 10px", fontSize: theme.fontSize2Xs }}>{approvalError}</div>
            )}

            <p style={{ ...eyebrow, margin: "18px 0 4px" }}>Events</p>
            {selected.events.length === 0 && <span style={mutedNote}>none yet</span>}
            {eventsNewestFirst.map((e) => {
              const tone = EVENT_TONE[e.status] ?? theme.textMuted;
              const isRunning = e.id === runningEventId;
              const statusLabel = e.status === "started" ? (isRunning ? "running" : "progress") : e.status;
              return (
                <div
                  key={e.id}
                  style={{
                    ...logRow,
                    // Status is carried by colour, not only by the word: a rail
                    // down the left edge, and failures also get a faint wash so
                    // they stand out when scanning a long run of progress rows.
                    borderLeft: `3px solid ${tone}`,
                    paddingLeft: 10,
                    background: e.status === "failed" ? tint(theme.danger, 7) : undefined,
                  }}
                >
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
                    <b style={{ color: theme.textPrimary }}>{STAGE_LABELS[e.stage] ?? e.stage}</b>
                    <span style={{ ...statusPill, display: "inline-flex", alignItems: "center", gap: 5, color: tone, background: tint(tone, 14) }}>
                      {isRunning && <Spinner color={tone} size={9} />}
                      {statusLabel}
                    </span>
                    <span style={{ color: theme.textMuted }}>{e.actor}</span>
                    {/* auto margin, not a "—" separator: the timestamp column
                        lines up down the log even when the actor names differ. */}
                    <span style={{ color: theme.textMuted, marginLeft: "auto" }}>{new Date(e.at).toLocaleString()}</span>
                  </div>
                  {e.detail && (
                    <div style={{ color: e.status === "failed" ? theme.textSecondary : theme.textMuted, marginTop: 3 }}>
                      {e.detail}
                    </div>
                  )}
                </div>
              );
            })}

            <p style={{ ...eyebrow, margin: "18px 0 4px" }}>Actions</p>
            {selected.approvals.length === 0 && <span style={mutedNote}>no one has acted on this batch yet</span>}
            {actionsNewestFirst.map((a) => {
              const action = describeAction(a);
              return (
                <div key={a.id} style={{ ...logRow, borderLeft: `3px solid ${action.tone}`, paddingLeft: 10 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{action.text}</span>
                    {QA_GATES.has(a.gate) && a.decision === "approved" && (
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: VIOLET, textTransform: "uppercase" }}>QA</span>
                    )}
                    <PersonChip name={a.approver} />
                    {a.note && <span style={{ color: theme.textMuted }}>{a.note}</span>}
                    <span style={{ color: theme.textMuted, marginLeft: "auto" }}>{new Date(a.at).toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      </div>
      </div>
      {retrainDialogOpen && (
        <RetrainSelectionDialog
          target={retrainTarget}
          busy={approving === "annotation_qa"}
          submitError={approvalError}
          onClearError={() => setApprovalError(null)}
          onChange={(target) => { setApprovalError(null); setRetrainTarget(target); }}
          onCancel={() => { setApprovalError(null); setRetrainDialogOpen(false); }}
          onConfirm={confirmRetrain}
        />
      )}
      {centralArtifactPreview && (
        <TrainingArtifactPreviewDialog
          artifact={centralArtifactPreview}
          onClose={() => setCentralArtifactPreview(null)}
        />
      )}
    </>
  );
}

// Section headings sit above the cards rather than inside them, so the two
// columns read as one row of headings; the nav rail already names the page,
// so there is no <h1> above these.
const sectionHeading: CSSProperties = {
  margin: 0, fontSize: 17, fontWeight: 700, color: theme.textPrimary,
};

const eyebrow: CSSProperties = {
  textTransform: "uppercase", fontSize: 10, fontWeight: 700,
  letterSpacing: "0.12em", color: theme.textMuted,
};

// Empty / loading states get the same card frame as real content, so a page
// with nothing in it still looks composed rather than half-rendered.
const placeholderCard: CSSProperties = {
  background: theme.bgCard, border: `1px dashed ${theme.border}`, borderRadius: theme.radius,
  padding: "18px 16px", color: theme.textMuted, fontSize: theme.fontSizeXs,
};

const logRow: CSSProperties = {
  fontSize: theme.fontSize2Xs, color: theme.textSecondary,
  padding: "5px 0", borderBottom: `1px solid ${theme.border}`,
};

// Event status -> colour. Progress ("started") is informational, not brand
// gold, so the accent stays reserved for the stage that is active right now.
const EVENT_TONE: Record<string, string> = {
  completed: theme.success,
  failed:    theme.danger,
  started:   theme.info,
  skipped:   theme.textMuted,
};

const statusPill: CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
  padding: "1px 6px", borderRadius: 3, lineHeight: "14px",
};

const mutedNote: CSSProperties = { color: theme.textMuted, fontSize: theme.fontSize2Xs };
